import dayjs from 'dayjs'

import type { BrandRanking, RankMetrics } from 'contexts/BrandRankingContext'
import { getBrandMetricsByPeriodicity } from 'contexts/BrandRankingContext/utils'
import type { Periodicity } from 'entities/periodicity'

import { PERIODICITIES } from './const'
import type { SoaDateFilter } from './types'

export const checkPeriodicity = (periodicity: Periodicity) => {
  return PERIODICITIES.includes(periodicity)
}

export const updateBrandsMetricsByPeriodicity = (
  brands: BrandRanking[],
  activeBrandId: string,
  periodicity: Periodicity
) => {
  return brands.map(brand => {
    const metricsByPeriodicity = getBrandMetricsByPeriodicity(brand, periodicity)
    const lastMetrics = metricsByPeriodicity[0]

    return {
      ...brand,
      active: !activeBrandId ? brand.own : brand.id === activeBrandId,
      rank: lastMetrics?.rank,
      rankVariation: lastMetrics?.rankVariation,
      score: lastMetrics?.score,
      variation: lastMetrics?.variation,
    }
  })
}

export const updateBrandsMetricsByDateFilter = (
  brands: BrandRanking[],
  soaDateFilter: SoaDateFilter,
  activeBrandId: string
) => {
  return brands.map(brand => {
    const metric = getBrandConsolidatedMetric(brand, soaDateFilter)

    return {
      ...brand,
      active: !activeBrandId ? brand.own : brand.id === activeBrandId,
      rank: metric?.rank,
      rankVariation: metric?.rankVariation,
      score: metric?.score,
      variation: metric?.variation,
    }
  })
}

export const parseBrandsRanking = (brands: BrandRanking[], activeBrandId: string, soaDateFilter: SoaDateFilter) => {
  const brandsWithUpdatedMetrics = updateBrandsMetricsByDateFilter(brands, soaDateFilter, activeBrandId)
  const sortedBrandsByRank = [...brandsWithUpdatedMetrics].sort((a, b) => a.rank - b.rank)
  return sortedBrandsByRank
}

const getMetricsRangeByDates = (metrics: RankMetrics[], dates: string[]): RankMetrics[] => {
  return metrics.filter(metric => dates.includes(metric.date))
}

export const getBrandMetricsByDateFilter = (brand: BrandRanking, soaDateFilter: SoaDateFilter): RankMetrics[] => {
  const metrics =
    getBrandMetricsByPeriodicity(brand, ['current'].includes(soaDateFilter.mode) ? 'daily' : 'monthly') ?? []

  if (soaDateFilter.mode === 'current') {
    const currentDate = metrics[0]?.date
    const currentMonth = dayjs(currentDate).utc().month()

    return metrics.filter(metric => dayjs(metric.date).utc().month() === currentMonth)
  }

  return getMetricsRangeByDates(metrics, soaDateFilter.dates ?? [])
}

export function getSoaDateFilterFromParams(searchParams: URLSearchParams, availableDates: string[]): SoaDateFilter {
  const datesParam = searchParams.get('dates')
  const rangeParam = searchParams.get('range')

  if (rangeParam) {
    const parsedRange = Number(rangeParam)
    const rangeDates = availableDates.slice(1, parsedRange + 1)

    return {
      mode: 'range',
      range: parsedRange,
      dates: rangeDates,
    }
  }

  if (datesParam) {
    const date = new Date(Number(datesParam))
    if (!isNaN(date.getTime())) {
      return {
        mode: 'unit',
        dates: [date.toISOString()],
      }
    }
  }

  const currentUtcStartOfMonth = dayjs().utc().startOf('month')
  return {
    mode: 'current',
    dates: availableDates.length ? [availableDates[0]] : [currentUtcStartOfMonth.toISOString()],
  }
}

export const getBrandConsolidatedMetric = (brand: BrandRanking, soaDateFilter: SoaDateFilter): RankMetrics | null => {
  if (!brand) return null

  if (soaDateFilter.mode === 'range') {
    if (soaDateFilter.range === 3) {
      return brand.last3MonthsMetric
    }
    if (soaDateFilter.range === 6) {
      return brand.last6MonthsMetric
    }
    if (soaDateFilter.range === 12) {
      return brand.last12MonthsMetric
    }
  }

  const monthlyMetrics = getBrandMetricsByPeriodicity(brand, 'monthly') ?? []
  const activeMetrics = getMetricsRangeByDates(monthlyMetrics, soaDateFilter.dates ?? [])

  return activeMetrics[0] || null
}
